import * as Sentry from "@sentry/react";
import { addEventProcessor } from "@sentry/core";
import React from "react";
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from "react-router-dom";

// Install lightweight network logger for Sentry envelopes
(function installEnvelopeSizeLogger() {
  try {
    const SENTRY_HOST = 'sentry-mjb.duckdns.org';

    // Patch fetch
    if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
      const originalFetch = window.fetch.bind(window);
      window.fetch = function patchedFetch(input, init) {
        try {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          const isSentryEnvelope = url.includes('/envelope') && url.includes(SENTRY_HOST);
          if (isSentryEnvelope) {
            let size = 0;
            let body = init && init.body;
            try {
              if (typeof body === 'string') {
                size = body ? new TextEncoder().encode(body).length : 0;
              } else if (body && typeof body.byteLength === 'number') {
                size = body.byteLength; // ArrayBuffer/TypedArray
              } else if (body && typeof body.size === 'number') {
                size = body.size; // Blob
              } else if (body && typeof body.getBoundary === 'function') {
                size = 0; // FormData unknown size
              }
            } catch (_) {
              // ignore
            }
            // eslint-disable-next-line no-console
            console.log('[SentrySize] envelope fetch size≈%d url=%s', size, url);
          }
        } catch (_) {
          // ignore
        }
        return originalFetch(input, init);
      };
    }

    // Patch XHR
    if (typeof window !== 'undefined' && typeof window.XMLHttpRequest !== 'undefined') {
      const OriginalXHR = window.XMLHttpRequest;
      function XHRInterceptor() {
        const xhr = new OriginalXHR();
        let requestUrl = '';
        const originalOpen = xhr.open;
        const originalSend = xhr.send;

        xhr.open = function patchedOpen(method, url) {
          try { requestUrl = String(url || ''); } catch (_) { requestUrl = ''; }
          return originalOpen.apply(xhr, arguments);
        };

        xhr.send = function patchedSend(data) {
          try {
            const isSentryEnvelope = requestUrl.includes('/envelope') && requestUrl.includes(SENTRY_HOST);
            if (isSentryEnvelope) {
              let size = 0;
              try {
                if (typeof data === 'string') {
                  size = new TextEncoder().encode(data).length;
                } else if (data && typeof data.byteLength === 'number') {
                  size = data.byteLength;
                } else if (data && typeof data.size === 'number') {
                  size = data.size;
                }
              } catch (_) {
                // ignore
              }
              // eslint-disable-next-line no-console
              console.log('[SentrySize] envelope xhr size≈%d url=%s', size, requestUrl);
            }
          } catch (_) {
            // ignore
          }
          return originalSend.apply(xhr, arguments);
        };

        return xhr;
      }
      window.XMLHttpRequest = XHRInterceptor;
    }

    // Patch navigator.sendBeacon
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const originalSendBeacon = navigator.sendBeacon.bind(navigator);
      navigator.sendBeacon = function patchedSendBeacon(url, data) {
        try {
          const target = String(url || '');
          const isSentryEnvelope = target.includes('/envelope') && target.includes(SENTRY_HOST);
          if (isSentryEnvelope) {
            let size = 0;
            try {
              if (typeof data === 'string') {
                size = new TextEncoder().encode(data).length;
              } else if (data && typeof data.byteLength === 'number') {
                size = data.byteLength;
              } else if (data && typeof data.size === 'number') {
                size = data.size;
              }
            } catch (_) {
              // ignore
            }
            // eslint-disable-next-line no-console
            console.log('[SentrySize] envelope beacon size≈%d url=%s', size, target);
          }
        } catch (_) {
          // ignore
        }
        return originalSendBeacon(url, data);
      };
    }
  } catch (_) {
    // ignore
  }
})();

// Lightweight helpers to measure approximate payload size
const defaultSentryMetrics = {
  totalBreadcrumbs: 0,
  consoleBreadcrumbs: 0,
  breadcrumbBytes: 0,
  consoleBytes: 0,
  lastEventBytes: 0,
  lastEventBreadcrumbBytes: 0,
  lastEventDroppedBreadcrumbs: 0,
};

let localSentryMetrics = { ...defaultSentryMetrics };

function getSentryMetrics() {
  try {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-underscore-dangle
      window.__sentryMetrics = window.__sentryMetrics || { ...defaultSentryMetrics };
      // eslint-disable-next-line no-underscore-dangle
      return window.__sentryMetrics;
    }
  } catch (_) {
    // no-op, fall through to local metrics
  }
  return localSentryMetrics;
}

function getByteLength(input) {
  try {
    const str = typeof input === 'string' ? input : JSON.stringify(input);
    if (typeof TextEncoder !== 'undefined') {
      return new TextEncoder().encode(str).length;
    }
    // Fallback for older environments
    // eslint-disable-next-line no-undef
    return new Blob([str]).size;
  } catch (_) {
    return 0;
  }
}

// Truncate Redux state to keep useful context while limiting size
export function truncateReduxState(state, maxDepth = 10, maxSize = 5000) {
  if (!state || typeof state !== 'object') {
    return state;
  }
  
  try {
    const stateStr = JSON.stringify(state);
    // If state fits within maxSize, return it as-is (no truncation)
    if (stateStr.length <= maxSize) {
      return state;
    }
    
    // State is too large - now we need to truncate
    // Keep ALL keys but truncate values to fit within size limit
    
    function createSampleData(obj, targetSize, depth = 0) {
      if (obj === null || obj === undefined || typeof obj !== 'object') {
        return obj;
      }
      
      // Prevent infinite recursion
      if (depth > maxDepth) {
        return '[Max depth reached]';
      }
      
      if (Array.isArray(obj)) {
        // For arrays, try to include as many items as possible
        const result = [];
        let currentSize = 2; // for brackets
        const minSizePerItem = 50; // Minimum size per item
        const maxItems = Math.min(obj.length, Math.floor(targetSize / minSizePerItem));
        
        for (let i = 0; i < maxItems && i < obj.length; i++) {
          const remainingSize = targetSize - currentSize - 10; // Reserve for closing bracket and comma
          const itemSample = createSampleData(obj[i], Math.max(minSizePerItem, remainingSize), depth + 1);
          const itemStr = JSON.stringify(itemSample);
          if (currentSize + itemStr.length + 2 <= targetSize) {
            result.push(itemSample);
            currentSize += itemStr.length + 2; // +2 for comma and space
          } else {
            // Try with smaller size
            const smallerSample = createSampleData(obj[i], Math.max(20, remainingSize - 20), depth + 1);
            const smallerStr = JSON.stringify(smallerSample);
            if (currentSize + smallerStr.length + 2 <= targetSize) {
              result.push(smallerSample);
              currentSize += smallerStr.length + 2;
            } else {
              result.push(`[Item ${i + 1} truncated]`);
              currentSize += 25; // Approximate size
            }
          }
        }
        
        if (obj.length > maxItems) {
          result.push(`[${obj.length - maxItems} more items...]`);
        }
        
        return result;
      }
      
      // For objects, ALWAYS include ALL keys, but truncate values
      const result = {};
      const keys = Object.keys(obj);
      
      if (keys.length === 0) {
        return {};
      }
      
      // Calculate overhead: braces, commas, key names, quotes
      const overhead = 2; // braces
      let keyOverhead = 0;
      for (const key of keys) {
        keyOverhead += key.length + 6; // "key": + comma and space (approx)
      }
      
      // Available size for values (after overhead)
      const availableSizeForValues = Math.max(100, targetSize - overhead - keyOverhead);
      const sizePerKey = Math.floor(availableSizeForValues / Math.max(1, keys.length));
      const minSizePerKey = Math.max(20, Math.floor(sizePerKey * 0.3)); // At least 30% of allocated size
      
      // Process all keys, truncating values as needed
      for (const key of keys) {
        const value = obj[key];
        let sampleValue;
        
        try {
          if (value === null || value === undefined) {
            sampleValue = value;
          } else if (typeof value === 'string') {
            // Truncate strings to fit within allocated size
            const maxStringLength = Math.max(50, sizePerKey - 20); // Reserve space for truncation marker
            if (value.length > maxStringLength) {
              sampleValue = value.substring(0, maxStringLength) + '...[truncated]';
            } else {
              sampleValue = value;
            }
          } else if (typeof value === 'number' || typeof value === 'boolean') {
            // Numbers and booleans are small, keep them as-is
            sampleValue = value;
          } else if (Array.isArray(value)) {
            // Recursively truncate arrays, ensure minimum size
            sampleValue = createSampleData(value, Math.max(minSizePerKey, sizePerKey), depth + 1);
          } else if (typeof value === 'object') {
            // Recursively truncate nested objects, but show at least some content
            const nestedSize = Math.max(minSizePerKey, sizePerKey);
            const nestedSample = createSampleData(value, nestedSize, depth + 1);
            
            // If nested object is still too large or empty, show at least keys
            if (typeof nestedSample === 'object' && nestedSample !== null) {
              const nestedKeys = Object.keys(value);
              if (nestedKeys.length > 0 && Object.keys(nestedSample).length === 0) {
                // Show at least the first few keys with minimal values
                sampleValue = {};
                for (let i = 0; i < Math.min(3, nestedKeys.length); i++) {
                  const nestedKey = nestedKeys[i];
                  const nestedValue = value[nestedKey];
                  if (typeof nestedValue === 'string') {
                    sampleValue[nestedKey] = nestedValue.substring(0, 30) + (nestedValue.length > 30 ? '...' : '');
                  } else if (typeof nestedValue === 'number' || typeof nestedValue === 'boolean') {
                    sampleValue[nestedKey] = nestedValue;
                  } else {
                    sampleValue[nestedKey] = `[${typeof nestedValue}]`;
                  }
                }
                if (nestedKeys.length > 3) {
                  sampleValue['[more_keys]'] = `${nestedKeys.length - 3} more...`;
                }
              } else {
                sampleValue = nestedSample;
              }
            } else {
              sampleValue = nestedSample;
            }
          } else {
            // Other types: convert to string and truncate
            const strValue = String(value);
            const maxLength = Math.max(50, sizePerKey - 20);
            sampleValue = strValue.length > maxLength 
              ? strValue.substring(0, maxLength) + '...[truncated]'
              : strValue;
          }
          
          // Always include the key, even if value is truncated
          result[key] = sampleValue;
        } catch (_) {
          // If we can't serialize, include key with error marker
          result[key] = '[Unserializable]';
        }
      }
      
      return result;
    }
    
    // Create truncated structure showing all keys
    const truncatedStructure = createSampleData(state, Math.floor(maxSize * 0.6)); // Use 60% for structure
    const truncatedStr = JSON.stringify(truncatedStructure);
    
    // Create result object with both truncated structure AND sample data
    const result = {
      '[info]': `State size: ${stateStr.length} bytes (truncated to fit within ${maxSize} bytes)`,
      '[truncated_structure]': truncatedStructure,
      '[sample_data]': {},
    };
    
    // Add sample data from original state (use remaining 30% of maxSize)
    const allKeys = Object.keys(state);
    const sampleDataSize = Math.floor(maxSize * 0.3); // Use 30% for sample data
    const sizePerSample = Math.floor(sampleDataSize / Math.max(1, Math.min(10, allKeys.length))); // Sample up to 10 keys
    
    // Include sample data from first few keys showing actual (non-truncated) values
    const sampleKeys = allKeys.slice(0, 10);
    let currentSampleSize = JSON.stringify(result).length;
    
    function createSampleValue(value, availableSize) {
      if (value === null || value === undefined) {
        return value;
      }
      if (typeof value === 'string') {
        const maxLen = Math.max(100, availableSize - 20);
        return value.length > maxLen ? value.substring(0, maxLen) + '...[sample]' : value;
      }
      if (typeof value === 'number' || typeof value === 'boolean') {
        return value;
      }
      if (Array.isArray(value)) {
        const samples = [];
        const sizePerItem = Math.floor(availableSize / Math.max(1, Math.min(3, value.length)));
        for (let i = 0; i < Math.min(3, value.length) && samples.length * sizePerItem < availableSize; i++) {
          samples.push(createSampleValue(value[i], sizePerItem));
        }
        if (value.length > 3) {
          samples.push(`[${value.length - 3} more items...]`);
        }
        return samples;
      }
      if (typeof value === 'object') {
        const sample = {};
        const objKeys = Object.keys(value).slice(0, 5);
        const sizePerObjKey = Math.floor(availableSize / Math.max(1, objKeys.length));
        for (const objKey of objKeys) {
          sample[objKey] = createSampleValue(value[objKey], sizePerObjKey);
        }
        if (Object.keys(value).length > 5) {
          sample['[more_keys]'] = `${Object.keys(value).length - 5} more...`;
        }
        return sample;
      }
      return String(value).substring(0, Math.min(100, availableSize - 20));
    }
    
    for (const key of sampleKeys) {
      try {
        const testResult = {
          ...result,
          '[sample_data]': {
            ...result['[sample_data]'],
            [key]: createSampleValue(state[key], sizePerSample),
          },
        };
        const testSize = JSON.stringify(testResult).length;
        
        if (testSize <= maxSize) {
          result['[sample_data]'][key] = createSampleValue(state[key], sizePerSample);
          currentSampleSize = testSize;
        } else {
          // Try with smaller sample
          const smallerSample = createSampleValue(state[key], Math.floor(sizePerSample * 0.5));
          const smallerTest = {
            ...result,
            '[sample_data]': {
              ...result['[sample_data]'],
              [key]: smallerSample,
            },
          };
          if (JSON.stringify(smallerTest).length <= maxSize) {
            result['[sample_data]'][key] = smallerSample;
          } else {
            break; // Can't fit more samples
          }
        }
      } catch (_) {
        // Skip if can't serialize
      }
    }
    
    // If result is still too large, prioritize structure over samples
    const finalStr = JSON.stringify(result);
    if (finalStr.length > maxSize) {
      // Remove some samples if needed
      const samples = result['[sample_data]'];
      const sampleKeysToRemove = Object.keys(samples).slice(5); // Keep first 5 samples
      for (const key of sampleKeysToRemove) {
        delete result['[sample_data]'][key];
        const testSize = JSON.stringify(result).length;
        if (testSize <= maxSize) {
          break;
        }
      }
    }
    
    return result;
  } catch (_) {
    return {
      '[error]': 'Unable to serialize Redux state',
      '[type]': typeof state,
    };
  }
}

Sentry.init({
  dsn: "https://65f865289cc2c35e2751ca74286e4f93@sentry-mjb.duckdns.org/8", // Replace with your actual DSN
  
  // Disable Sentry
  enabled: true,

  // Adds request headers and IP for users, for more info visit:
  // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
  sendDefaultPii: true,

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
  tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: [
    /^\//, // Match relative paths (current origin)
    /^https?:\/\/localhost:8082/,
    /^https?:\/\/51\.254\.213\.230:9192/,
    /^https?:\/\/localhost:8393/,
    /^https?:\/\/localhost:7091/,
    /^https?:\/\/mysolarlending\.com:8090/,
    /^https?:\/\/localhost:7095/,
    /^https?:\/\/jesuislaoulonnesoupsonnepas\.ovh:8396/,
    // Including PUBLIC_URL just in case, although relative paths might cover it
    /^https?:\/\/myjoulebox\.com:8000/,
    /^https?:\/\/app\.myjoulebox\.com/,
    /^http?:\/\/localhost:9000/,
    /^https?:\/\/app.mjb\.ovh/,
    

  ],

  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  // Learn more at
  // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration

  // Enable logs to be sent to Sentry
  enableLogs: false,

  // Replay sample rates
  replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0.5, // 10% in production, 100% in development
  replaysOnErrorSampleRate: 1.0, // Always record when errors occur
  environment: process.env.NODE_ENV || 'development',
  maxBreadcrumbs: 50,
  attachStacktrace: true,

  // Filter and sanitize breadcrumbs to avoid oversized payloads on noisy pages
  beforeBreadcrumb(crumb, hint) {
    try {
      const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
      const metrics = getSentryMetrics();

      // Always record metrics for incoming crumb (before any pruning)
      metrics.totalBreadcrumbs += 1;
      metrics.breadcrumbBytes += getByteLength(crumb);

      // Ignore our own measurement logs to prevent feedback loops
      if (crumb.category === 'console') {
        const firstArg = Array.isArray(crumb.data?.arguments) ? crumb.data.arguments[0] : undefined;
        if (typeof firstArg === 'string' && firstArg.startsWith('[SentrySize]')) return null;
      }

      // Drop very noisy console breadcrumbs on /adminpage/store
      if (path.startsWith('/adminpage/store')) {
        // Remove low-signal console logs
        if (crumb.category === 'console' && ['log', 'debug', 'info'].includes(crumb.level)) {
          if (crumb.category === 'console') {
            metrics.consoleBreadcrumbs += 1;
            metrics.consoleBytes += getByteLength(crumb.data?.arguments ?? crumb.message ?? '');
          }
          return null;
        }

        // Reduce size of remaining console breadcrumb arguments
        if (crumb.category === 'console' && crumb.data && Array.isArray(crumb.data.arguments)) {
          const MAX_ARG_LEN = 300;
          const MAX_ARGS = 3;
          const safeArgs = crumb.data.arguments.slice(0, MAX_ARGS).map((arg) => {
            try {
              const str = typeof arg === 'string' ? arg : JSON.stringify(arg);
              return str.length > MAX_ARG_LEN ? str.slice(0, MAX_ARG_LEN) + '…' : str;
            } catch (_) {
              return '[Unserializable]';
            }
          });
          return {
            ...crumb,
            data: { ...crumb.data, arguments: safeArgs },
          };
        }

        // Drop very noisy DOM/history crumbs on this page
        if (['ui.click', 'ui.input', 'navigation'].includes(crumb.category)) {
          return null;
        }
      }

      return crumb;
    } catch (_) {
      return crumb;
    }
  },

  // Capture failed HTTP requests as errors
  // beforeSend(event, hint) {
  //   const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  //   const metrics = getSentryMetrics();

  //   const origCount = Array.isArray(event.breadcrumbs) ? event.breadcrumbs.length : 0;
  //   const origBreadcrumbsBytes = getByteLength(event.breadcrumbs || []);
  //   const origEventBytes = getByteLength(event);

  //   // On noisy page, aggressively prune breadcrumbs to keep payload small
  //   if (path.startsWith('/adminpage/store') && Array.isArray(event.breadcrumbs)) {
  //     const importantLevels = new Set(['error', 'warning']);
  //     const importantCategories = new Set(['http', 'xhr', 'fetch', 'sentry']);

  //     const pruned = [];
  //     for (const crumb of event.breadcrumbs) {
  //       const keepByLevel = crumb.level && importantLevels.has(crumb.level);
  //       const keepByCategory = crumb.category && importantCategories.has(crumb.category);
  //       if (keepByLevel || keepByCategory) {
  //         pruned.push(crumb);
  //       }
  //     }

  //     // Fallback: if pruning removed everything, keep the last few crumbs
  //     const finalCrumbs = (pruned.length > 0 ? pruned : event.breadcrumbs.slice(-20)).slice(-50);
  //     event.breadcrumbs = finalCrumbs;
  //   }

    // Update metrics and log size snapshot
  //   const newCount = Array.isArray(event.breadcrumbs) ? event.breadcrumbs.length : 0;
  //   const dropped = Math.max(0, origCount - newCount);
  //   metrics.lastEventBreadcrumbBytes = getByteLength(event.breadcrumbs || []);
  //   metrics.lastEventBytes = getByteLength(event);
  //   metrics.lastEventDroppedBreadcrumbs = dropped;

  //   // eslint-disable-next-line no-console
  //   console.log(
  //     '[SentrySize] path=%s origCrumbs=%d newCrumbs=%d dropped=%d crumbsBytes≈%d→%d eventBytes≈%d→%d',
  //     path,
  //     origCount,
  //     newCount,
  //     dropped,
  //     origBreadcrumbsBytes,
  //     metrics.lastEventBreadcrumbBytes,
  //     origEventBytes,
  //     metrics.lastEventBytes,
  //   );

  //   // Log HTTP error breadcrumbs for visibility during debugging
  //   const breadcrumbs = event.breadcrumbs || [];
  //   const httpErrors = breadcrumbs.filter((crumb) => crumb.category === 'fetch' || crumb.category === 'xhr');
  //   if (httpErrors.length > 0) {
  //     httpErrors.forEach((crumb) => {
  //       if (crumb.data?.status_code >= 400) {
  //         // eslint-disable-next-line no-console
  //         console.log('[Sentry] Capturing HTTP error:', crumb);
  //       }
  //     });
  //   }

  //   return event;
  // },
  beforeSend(event, hint) {
    // Remove or truncate large fields
    //console.log('event', event);
    
    // Check if this is a feedback event
    const isFeedbackEvent = event.tags?.feedback || 
                           event.contexts?.feedback ||
                           hint?.attachments?.some(att => att.filename?.includes('feedback')) ||
                           event.type === 'user_report';
    
    if (event.request && event.request.data && event.request.data.length > 1000) {
      event.request.data = '[Truncated]';
    }
    // Remove large extra data
    if (event.extra) {
      try {
        const extraSize = JSON.stringify(event.extra).length;
        if (extraSize > 10000) {
          // For feedback events, be more aggressive
          if (isFeedbackEvent) {
            event.extra = {};
          } else {
            // Keep only small extra fields
            const cleanedExtra = {};
            Object.keys(event.extra).forEach(key => {
              const value = event.extra[key];
              try {
                const size = JSON.stringify(value).length;
                if (size < 2000) {
                  cleanedExtra[key] = value;
                }
              } catch (_) {
                // Skip unserializable data
              }
            });
            event.extra = cleanedExtra;
          }
        }
      } catch (_) {
        event.extra = {};
      }
    }
    
    // Truncate Redux state in contexts if present (can be very large)
    if (event.contexts) {
      // Truncate Redux state in contexts
      if (event.contexts.state && event.contexts.state.type === 'redux') {
        if (event.contexts.state.value) {
          event.contexts.state.value = truncateReduxState(event.contexts.state.value, 2, 3000);
        }
      }
      // Also check for Redux state in other context keys
      Object.keys(event.contexts).forEach(key => {
        const context = event.contexts[key];
        if (context && typeof context === 'object' && context.type === 'redux') {
          if (context.value) {
            event.contexts[key].value = truncateReduxState(context.value, 2, 3000);
          }
        }
      });
    }
    
    // Truncate Redux state in extra data
    if (event.extra) {
      Object.keys(event.extra).forEach(key => {
        if (key.toLowerCase().includes('redux') || key.toLowerCase().includes('state')) {
          const value = event.extra[key];
          try {
            const size = JSON.stringify(value).length;
            if (size > 5000) {
              // Truncate instead of removing
              event.extra[key] = truncateReduxState(value, 2, 5000);
            }
          } catch (_) {
            // If can't serialize, remove it
            delete event.extra[key];
          }
        }
      });
    }
    
    // For feedback events, limit breadcrumbs more aggressively
    if (isFeedbackEvent && Array.isArray(event.breadcrumbs) && event.breadcrumbs.length > 20) {
      const important = event.breadcrumbs.filter(
        crumb => crumb.level === 'error' || crumb.level === 'warning' || 
                 crumb.category === 'http' || crumb.category === 'xhr' || crumb.category === 'fetch'
      );
      event.breadcrumbs = important.length > 0 
        ? important.slice(-20) 
        : event.breadcrumbs.slice(-20);
    }
    
    return event;
  },

  integrations: [
    // If you're using react router, use the integration for your react router version instead.
    // Learn more at
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
      // Enable automatic HTTP request tracking
      traceFetch: true,
      traceXHR: true,
      enableHTTPTimings: true,
    }),
    // Capture HTTP errors as breadcrumbs and errors
    Sentry.breadcrumbsIntegration({
      console: true,
      dom: true,
      fetch: true,
      history: true,
      sentry: true,
      xhr: true,
    }),
    Sentry.feedbackIntegration({
      // Basic configuration
      colorScheme: "system",
      autoInject: true,
      showBranding: false,

      // Screenshot functionality
      enableScreenshot: true,
      addScreenshotButtonLabel: "Ajouter une capture d'écran",
      removeScreenshotButtonLabel: "Supprimer la capture d'écran",

      // Text customizations for translation
      buttonLabel: "Signaler un bug",
      formTitle: "Signaler un bug",
      submitButtonLabel: "Envoyer le rapport",
      cancelButtonLabel: "Annuler",
      nameLabel: "Nom",
      namePlaceholder: "Votre nom",
      emailLabel: "Email",
      emailPlaceholder: "votre.email@exemple.com",
      messageLabel: "Description",
      messagePlaceholder: "Quel est le bug ? Que s'est-il passé et à quoi vous attendiez-vous ?",
      successMessageText: "Merci pour votre rapport !",
      confirmButtonLabel: "Confirmer",
      isRequiredLabel: "(requis)",

      // Screenshot configuration
      screenshotOptions: {
        quality: 0.8, // Image quality (0-1)
        format: 'image/png', // Image format
        width: window.innerWidth, // Screenshot width
        height: window.innerHeight, // Screenshot height
      },
      
      // Limit context data sent with feedback
      useSentryUser: {
        email: true,
        name: true,
      },
    }),
    Sentry.replayIntegration({
      // Privacy settings - Show more text instead of masking everything
      maskAllText: false, // Allow text to be visible
      maskAllInputs: true, // Still mask input fields for privacy
      blockAllMedia: false, // Allow images and videos
      
      // Only mask specific sensitive elements
      mask: [
        'input[type="password"]', // Only mask password fields
        'input[name*="password"]', // Only mask password-related inputs
        '.sensitive-data', // Only mask elements marked as sensitive
        '[data-sensitive]' // Only mask elements with data-sensitive attribute
      ],
      
      // Block only truly sensitive content
      block: [
        '.sentry-block', // Block elements explicitly marked to block
        '[data-sentry-block]', // Block elements with data attribute
        '.private-content' // Block private content areas
      ],
      
      // Ignore elements that don't need to be recorded
      ignore: [
        '.sentry-ignore', // Ignore elements marked to ignore
        '[data-sentry-ignore]' // Ignore elements with data attribute
      ],
      
      // Session settings
      stickySession: true, // Keep session across page reloads
      maxReplayDuration: 60000, // Maximum replay duration in milliseconds (60 seconds)
      
      // Performance settings
      slimDOMOptions: 'all', // Optimize DOM recording
      inlineStylesheet: true, // Embed styles for better replay
      collectFonts: true, // Load custom fonts
      inlineImages: false, // Load images remotely to save bandwidth
    }),
  ],
}); 

// Ensure event sanitization always runs, even if beforeSend isn't invoked
addEventProcessor((event, hint) => {
  //console.log('event', event);
  //console.log('hint', hint);
  try {
    // Check if this is a feedback event
    const isFeedbackEvent = event?.tags?.feedback || 
                           event?.contexts?.feedback ||
                           hint?.attachments?.some(att => att.filename?.includes('feedback')) ||
                           event?.type === 'user_report';
    
    if (event && event.request && event.request.data && typeof event.request.data === 'string' && event.request.data.length > 1000) {
      event.request.data = '[Truncated]';
    }
    if (event && event.extra) {
      try {
        const extraSize = JSON.stringify(event.extra).length;
        if (extraSize > 10000) {
          // For feedback events, remove all extra data
          if (isFeedbackEvent) {
            event.extra = {};
          } else {
            // Keep only small extra fields
            const cleanedExtra = {};
            Object.keys(event.extra).forEach(key => {
              const value = event.extra[key];
              try {
                const size = JSON.stringify(value).length;
                if (size < 2000) {
                  cleanedExtra[key] = value;
                }
              } catch (_) {
                // Skip unserializable data
              }
            });
            event.extra = cleanedExtra;
          }
        }
      } catch (_) {
        // If extra isn't serializable, drop it to avoid large payloads
        event.extra = {};
      }
    }
    
    // Truncate Redux state in contexts
    if (event && event.contexts) {
      if (event.contexts.state && event.contexts.state.type === 'redux') {
        if (event.contexts.state.value) {
          event.contexts.state.value = truncateReduxState(event.contexts.state.value, 2, 3000);
        }
      }
      // Truncate any other Redux-related contexts
      Object.keys(event.contexts).forEach(key => {
        const context = event.contexts[key];
        if (context && typeof context === 'object' && context.type === 'redux') {
          if (context.value) {
            event.contexts[key].value = truncateReduxState(context.value, 2, 3000);
          }
        }
      });
    }
    
    // For feedback events, be extra aggressive about removing large data
    if (isFeedbackEvent) {
      // Limit breadcrumbs
      if (Array.isArray(event.breadcrumbs) && event.breadcrumbs.length > 20) {
        const important = event.breadcrumbs.filter(
          crumb => crumb.level === 'error' || crumb.level === 'warning' || 
                   crumb.category === 'http' || crumb.category === 'xhr' || crumb.category === 'fetch'
        );
        event.breadcrumbs = important.length > 0 
          ? important.slice(-20) 
          : event.breadcrumbs.slice(-20);
      }
      
      // Remove large tags
      if (event.tags) {
        const essentialTags = {};
        Object.keys(event.tags).forEach(key => {
          const value = event.tags[key];
          if (typeof value === 'string' && value.length < 200) {
            essentialTags[key] = value;
          }
        });
        event.tags = essentialTags;
      }
    }
  } catch (_) {
    // no-op
  }
  return event;
});